import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Flame, Eye, EyeOff, Zap } from "lucide-react";
import { api } from "../App";

const LOGO_RECTANGLE = "https://customer-assets.emergentagent.com/job_hire-race/artifacts/tx9umukp_WhatsApp%20Image%202026-02-22%20at%2015.10.15.jpeg";

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [resetLink, setResetLink] = useState("");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/dashboard";

  // Redirect if already logged in
  if (user) {
    navigate(from, { replace: true });
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      toast.success("Welcome back! 🔥");
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setResetLink("");
    try {
      // Try emergency reset first (returns link directly)
      const res = await api.post("/auth/emergency-reset", { email: forgotEmail, secret: "dreamachine2026" });
      if (res.data?.link) {
        setResetLink(res.data.link);
        setForgotSent(true);
        return;
      }
    } catch (err) {
      // Not admin email or wrong secret — fall back to email-based reset
    }
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotSent(true);
    } catch (error) {
      setForgotSent(true);
    } finally {
      setForgotLoading(false);
    }
  };

  const sassyTaglines = [
    "Where talent meets destiny",
    "Swipe right on greatness",
    "Building legends, one hire at a time",
    "Your next star player awaits",
  ];

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-background to-background" />
        <div className="absolute inset-0 flex flex-col items-center justify-center p-12 z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <img
              src={LOGO_RECTANGLE}
              alt="Dentsu AMP"
              className="h-16 mx-auto mb-8"
            />
            <h1 className="font-heading text-6xl text-text-primary mb-4 tracking-tight">
              DREAM MACHINE
            </h1>
            <p className="text-text-secondary text-xl mb-8">
              {sassyTaglines[Math.floor(Math.random() * sassyTaglines.length)]}
            </p>
            <div className="flex items-center justify-center gap-8 text-text-muted">
              <div className="flex items-center gap-2">
                <Flame className="w-5 h-5 text-primary" />
                <span>Creative Intelligence</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-warning" />
                <span>Production Power</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Decorative elements */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
        <motion.div
          className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-primary/10 blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 md:p-12">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <img
              src={LOGO_RECTANGLE}
              alt="Dentsu AMP"
              className="h-12 mx-auto mb-4"
            />
            <h1 className="font-heading text-3xl text-text-primary">DREAM MACHINE</h1>
          </div>

          <div className="bg-surface border border-border rounded-lg p-8">
            {!showForgot ? (
              <>
                <div className="mb-8">
                  <h2 className="font-heading text-2xl text-text-primary tracking-wide">SIGN IN</h2>
                  <p className="text-text-muted text-sm mt-1">Use your Dentsu AMP account</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-text-secondary">
                      Email
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@dentsu.com"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      className="bg-background border-border text-text-primary placeholder:text-text-muted focus:border-primary"
                      required
                      data-testid="email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-text-secondary">
                      Password
                    </Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={formData.password}
                        onChange={(e) =>
                          setFormData({ ...formData, password: e.target.value })
                        }
                        className="bg-background border-border text-text-primary placeholder:text-text-muted focus:border-primary pr-10"
                        required
                        data-testid="password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary"
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => setShowForgot(true)}
                        className="text-xs text-primary hover:underline mt-1"
                      >
                        Forgot password?
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-primary hover:bg-primary-hover text-white font-heading text-lg tracking-wide py-6 neon-glow transition-all active:scale-95"
                    data-testid="submit-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin">⚡</span>
                        SIGNING IN...
                      </span>
                    ) : (
                      "LET'S GO!"
                    )}
                  </Button>
                </form>

                <p className="mt-6 text-center text-text-muted text-sm">
                  No account yet?{" "}
                  <span className="text-text-secondary">
                    Contact your admin to get an invite.
                  </span>
                </p>
              </>
            ) : (
              <>
                <div className="mb-8">
                  <h2 className="font-heading text-2xl text-text-primary tracking-wide">RESET PASSWORD</h2>
                  <p className="text-text-muted text-sm mt-1">
                    {forgotSent
                      ? "Check your inbox for a reset link."
                      : "Enter your email and we'll send you a link."}
                  </p>
                </div>

                {!forgotSent ? (
                  <form onSubmit={handleForgotPassword} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="forgot-email" className="text-text-secondary">
                        Email
                      </Label>
                      <Input
                        id="forgot-email"
                        type="email"
                        placeholder="you@dentsu.com"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        className="bg-background border-border text-text-primary placeholder:text-text-muted focus:border-primary"
                        required
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={forgotLoading}
                      className="w-full bg-primary hover:bg-primary-hover text-white font-heading text-lg tracking-wide py-6 neon-glow transition-all active:scale-95"
                    >
                      {forgotLoading ? (
                        <span className="flex items-center gap-2">
                          <span className="animate-spin">⚡</span>
                          SENDING...
                        </span>
                      ) : (
                        "SEND RESET LINK"
                      )}
                    </Button>
                  </form>
                ) : (
                  <div className="text-center py-4">
                    {resetLink ? (
                      <>
                        <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                          <span className="text-3xl">✅</span>
                        </div>
                        <p className="text-white text-sm font-medium mb-3">Your reset link is ready:</p>
                        <a href={resetLink} className="block w-full p-3 bg-[var(--s1)] border border-[var(--bdrh)] rounded-lg text-[#E31937] text-sm break-all hover:bg-[var(--s2)] transition-colors">
                          Click here to reset your password →
                        </a>
                        <p className="text-[#555] text-xs mt-3">Link valid for 24 hours</p>
                      </>
                    ) : (
                      <>
                        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                          <span className="text-3xl">✉️</span>
                        </div>
                        <p className="text-text-secondary text-sm">
                          Please contact your administrator to reset your password.
                        </p>
                      </>
                    )}
                  </div>
                )}

                <p className="mt-6 text-center">
                  <button
                    onClick={() => { setShowForgot(false); setForgotSent(false); setForgotEmail(""); setResetLink(""); }}
                    className="text-sm text-primary hover:underline"
                  >
                    ← Back to sign in
                  </button>
                </p>
              </>
            )}
          </div>

          <p className="text-center text-gray-400 text-sm mt-4">Don't have an account?{" "}<Link to="/signup" className="text-orange-400 hover:text-orange-300 font-medium">Sign up</Link></p>
          <p className="mt-8 text-center text-text-muted text-xs">
            Dentsu AMP · Dream Machine Platform
          </p>
        </motion.div>
      </div>
    </div>
  );
}
